import { useCallback, useEffect, useRef, useState } from 'react';
import * as claus from 'src/_shared/services/claus/endpoints';

/**
 * Minimal async-data hook in the plain useState/useEffect style the app uses (no react-query).
 * Returns { loading, data, error, reload }. `reload(...args)` re-runs `fn` and returns its promise.
 */
export function useAsync(fn, deps = [], { immediate = true } = {}) {
	const [state, setState] = useState({ loading: immediate, data: null, error: null });
	const mounted = useRef(true);

	// deps are caller-supplied by design (this build's lint doesn't register react-hooks — a
	// disable directive for it errors as an unknown rule, so don't add one back)
	const run = useCallback(async (...args) => {
		setState((s) => ({ ...s, loading: true, error: null }));
		try {
			const data = await fn(...args);
			if (mounted.current) setState({ loading: false, data, error: null });
			return data;
		} catch (error) {
			if (mounted.current) setState({ loading: false, data: null, error });
			throw error;
		}
	}, deps);

	useEffect(() => {
		mounted.current = true;
		if (immediate) run().catch(() => {});
		return () => {
			mounted.current = false;
		};
	}, [run, immediate]);

	return { ...state, reload: run, setData: (data) => setState((s) => ({ ...s, data })) };
}

export const useTemplates = (type) => useAsync(() => claus.listTemplates(type), [type]);

export const useOrgIntegrations = (orgId) =>
	useAsync(() => (orgId ? claus.listIntegrations(orgId) : Promise.resolve({ items: [] })), [orgId]);

export const useIntegration = (orgId, id) =>
	useAsync(
		() => (orgId && id ? claus.getIntegration(orgId, id) : Promise.resolve(null)),
		[orgId, id],
	);
